import { useNavigate } from 'react-router-dom';
import PageNavbar from '../../../components/feature/PageNavbar';

export default function DWallPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <PageNavbar />

      {/* Hero Section */}
      <section className="py-12 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Immersive Full-Body Rehabilitation
              </h2>
              <p className="text-gray-700 text-lg leading-relaxed mb-6">
                D-WALL is a revolutionary large-scale interactive rehabilitation system that combines advanced motion tracking technology with an immersive visual interface. The wall-mounted display and integrated floor platform create an engaging environment for comprehensive full-body rehabilitation exercises.
              </p>
              <p className="text-gray-700 leading-relaxed">
                By tracking movements in real-time and providing instant visual feedback through motivating games and activities, D-WALL transforms traditional therapy into an engaging experience that promotes better outcomes and higher patient satisfaction across diverse rehabilitation needs.
              </p>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-xl">
              <img
                src="https://public.readdy.ai/ai/img_res/d778901ea059fbd62f541fcbe416fbda.jpg"
                alt="D-WALL Interactive Rehabilitation System"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-16 px-6">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Key Features</h2>
          
          <div className="space-y-8">
            <div className="border-l-4 border-emerald-600 pl-6">
              <h3 className="text-xl font-bold text-gray-900 mb-2">Motion Tracking Technology</h3>
              <p className="text-gray-700 leading-relaxed">
                Advanced sensors capture full-body movements in real-time, providing precise tracking for accurate assessment and feedback.
              </p>
            </div>

            <div className="border-l-4 border-emerald-600 pl-6">
              <h3 className="text-xl font-bold text-gray-900 mb-2">Large Interactive Display</h3>
              <p className="text-gray-700 leading-relaxed">
                Wall-mounted screen with immersive visual interface that engages patients in motivating rehabilitation activities.
              </p>
            </div>

            <div className="border-l-4 border-emerald-600 pl-6">
              <h3 className="text-xl font-bold text-gray-900 mb-2">Floor Pressure Platform</h3>
              <p className="text-gray-700 leading-relaxed">
                Integrated pressure-sensitive floor mat tracks weight distribution, balance, and stepping patterns during exercises.
              </p>
            </div>

            <div className="border-l-4 border-emerald-600 pl-6">
              <h3 className="text-xl font-bold text-gray-900 mb-2">Interactive Games</h3>
              <p className="text-gray-700 leading-relaxed">
                Extensive library of therapeutic games designed to target specific movement patterns and functional goals.
              </p>
            </div>

            <div className="border-l-4 border-emerald-600 pl-6">
              <h3 className="text-xl font-bold text-gray-900 mb-2">Performance Analytics</h3>
              <p className="text-gray-700 leading-relaxed">
                Comprehensive data collection and analysis providing objective measurements of patient progress and outcomes.
              </p>
            </div>

            <div className="border-l-4 border-emerald-600 pl-6">
              <h3 className="text-xl font-bold text-gray-900 mb-2">Multi-Patient Capability</h3>
              <p className="text-gray-700 leading-relaxed">
                Supports individual and group therapy sessions, promoting social interaction and competitive motivation.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Training Modes */}
      <section className="py-16 px-6 border-t border-gray-100">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Training Modes</h2>
          
          <div className="grid md:grid-cols-2 gap-x-12 gap-y-8">
            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Upper Extremity Training</h3>
              <p className="text-gray-700 leading-relaxed">
                Reaching, grasping, and coordination exercises targeting arm and shoulder function with visual feedback.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Lower Extremity Training</h3>
              <p className="text-gray-700 leading-relaxed">
                Stepping, weight shifting, and gait training activities to improve leg strength and walking ability.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Balance and Postural Control</h3>
              <p className="text-gray-700 leading-relaxed">
                Static and dynamic balance exercises with real-time feedback to enhance stability and reduce fall risk.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Cognitive Training</h3>
              <p className="text-gray-700 leading-relaxed">
                Dual-task activities combining physical movement with cognitive challenges for comprehensive rehabilitation.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Cardiovascular Conditioning</h3>
              <p className="text-gray-700 leading-relaxed">
                Aerobic exercises and endurance training activities to improve cardiovascular fitness and stamina.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Functional Activities</h3>
              <p className="text-gray-700 leading-relaxed">
                Task-oriented exercises simulating real-world activities to improve independence in daily living.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 px-6 border-t border-gray-100">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Key Benefits</h2>
          
          <div className="space-y-6">
            <p className="text-gray-700 leading-relaxed">
              <strong className="text-gray-900">Enhanced Motivation:</strong> Engaging visual interface and game-based exercises increase patient participation and adherence to therapy programs.
            </p>

            <p className="text-gray-700 leading-relaxed">
              <strong className="text-gray-900">Objective Measurement:</strong> Precise tracking and data collection provide quantifiable evidence of progress and treatment effectiveness.
            </p>

            <p className="text-gray-700 leading-relaxed">
              <strong className="text-gray-900">Versatile Applications:</strong> Suitable for diverse patient populations and conditions, from neurological to orthopedic rehabilitation.
            </p>

            <p className="text-gray-700 leading-relaxed">
              <strong className="text-gray-900">Efficient Therapy:</strong> Enables therapists to work with multiple patients simultaneously while maintaining quality care and supervision.
            </p>
          </div>
        </div>
      </section>

      {/* Clinical Applications */}
      <section className="py-16 px-6 border-t border-gray-100">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Clinical Applications</h2>
          
          <div className="columns-2 md:columns-3 lg:columns-4 gap-6 text-gray-700">
            <p className="mb-3">Stroke Rehabilitation</p>
            <p className="mb-3">Parkinson's Disease</p>
            <p className="mb-3">Multiple Sclerosis</p>
            <p className="mb-3">Traumatic Brain Injury</p>
            <p className="mb-3">Spinal Cord Injury</p>
            <p className="mb-3">Cerebral Palsy</p>
            <p className="mb-3">Balance Disorders</p>
            <p className="mb-3">Orthopedic Conditions</p>
            <p className="mb-3">Sports Injury Recovery</p>
            <p className="mb-3">Geriatric Rehabilitation</p>
            <p className="mb-3">Pediatric Therapy</p>
            <p className="mb-3">Fall Prevention Programs</p>
          </div>
        </div>
      </section>

      {/* Therapy Process */}
      <section className="py-16 px-6 border-t border-gray-100">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Therapy Process</h2>
          
          <div className="space-y-8">
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Initial Assessment</h3>
              <p className="text-gray-700 leading-relaxed">
                Baseline evaluation using standardized tests to measure current functional abilities and establish treatment goals.
              </p>
            </div>

            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Program Customization</h3>
              <p className="text-gray-700 leading-relaxed">
                Therapist selects appropriate exercises and adjusts difficulty levels based on patient capabilities and objectives.
              </p>
            </div>

            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Interactive Training</h3>
              <p className="text-gray-700 leading-relaxed">
                Patient performs exercises with real-time visual feedback and guidance, promoting proper movement patterns.
              </p>
            </div>

            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Progress Monitoring</h3>
              <p className="text-gray-700 leading-relaxed">
                Continuous tracking of performance metrics with regular reassessment to adjust treatment plans and demonstrate improvement.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-6 border-t border-gray-100">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Transform Your Rehabilitation Experience</h2>
          <p className="text-gray-700 text-lg leading-relaxed mb-8">
            D-WALL brings together cutting-edge technology and evidence-based therapy to create an engaging, effective rehabilitation environment. The combination of motion tracking, interactive games, and comprehensive data analysis helps patients achieve better outcomes while making therapy more enjoyable and motivating.
          </p>
          <button
            onClick={() => navigate('/equipment')}
            className="inline-flex items-center gap-2 px-8 py-3 bg-emerald-600 text-white rounded-full hover:bg-emerald-700 transition-colors shadow-lg hover:shadow-xl cursor-pointer whitespace-nowrap"
          >
            <span>Explore More Equipment</span>
            <i className="ri-arrow-right-line"></i>
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white py-8 px-6 border-t border-gray-100">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-gray-500 text-sm">
            © 2025 Accelerated Recovery Centre. All rights reserved.
          </p>
          <a 
            href="https://readdy.ai/?ref=logo" 
            target="_blank" 
            rel="nofollow noopener noreferrer"
            className="text-gray-400 text-xs hover:text-gray-600 transition-colors mt-2 inline-block"
          >
            Powered by Readdy
          </a>
        </div>
      </footer>
    </div>
  );
}
